import React from 'react';
import {
    Box,
    Flex,
    Heading,
    Icon,
    ListIcon,
    ListItem,
    Text,
    UnorderedList,
    useMultiStyleConfig,
} from '@chakra-ui/react';
import { RichText } from 'frontend/wagtail/components/RichText';
import { FaCheck, FaTag } from 'react-icons/fa';
import {ColorButton} from "frontend/core/components/ColorButton";

/**
 * React component for Product Cards displaying different options in the order tunnel.
 *
 * Valid props are:
 * - title - The name of the product.
 * - price - The price that the user will pay for the product.
 * - originalPrice - The "original price" to show slashed out in favor of the actual price.
 * - pricePeriodText - Text displayed underneath the price explaining the duration of the price.
 * - buttonText - The text to show on the button to choose the product.
 * - extraProductText - Text displayed beneath the products subscribe button and above the features list.
 * - primaryColor - The accent color for the product.
 * - specialOffer - The callout to be shown above the card indicating that it is a special offer.
 * - specialOfferColor - The color to be displayed on the special offer callout for the card.
 * - features - A list of the product's features.
 * - buttonOnClick - specifies the onClick function for the subscribe button.
 * - buttonName - specifies the `name` attribute on the subscribe button.
 *
 * @param props
 * @returns {JSX.Element}
 */
export const ProductCard = (props) => {
    const {
        title,
        price,
        originalPrice,
        pricePeriodText,
        buttonText,
        primaryColor,
        specialOffer,
        specialOfferColor,
        features,
        buttonOnClick,
        buttonName,
        extraProductText,
    } = props;

    const hasSpecialOffer = (!!specialOffer)

    const styles = useMultiStyleConfig("ProductCard", {
        hasSpecialOffer,
        primaryColor,
        specialOfferColor
    });

    return (
        <Box sx={styles.productCardWrapper}>
            <Flex sx={styles.specialOffer}>
                <Icon as={FaTag}/>
                <Text pl={"1ch"} sx={styles.specialOfferText}>{specialOffer}</Text>
            </Flex>
            <Flex sx={styles.cardContainer}>
                <Heading sx={styles.title}>{title}</Heading>
                <Flex sx={styles.pricing}>
                    {originalPrice &&
                    <Text sx={styles.originalPrice}>{originalPrice}</Text>}
                    <Text>{price}</Text>
                </Flex>
                <Flex sx={styles.pricingPeriod}>
                    {pricePeriodText && <Text>{pricePeriodText}</Text>}
                </Flex>
                <ColorButton
                    buttonColor={primaryColor}
                    name={buttonName}
                    onClick={buttonOnClick}
                    sx={styles.subscribeButton}
                >
                    {buttonText}
                </ColorButton>
                {extraProductText && (
                    <RichText data={extraProductText} sx={styles.extraProductText} />
                )}
                {features && // Display the list of features if provided.
                <UnorderedList sx={styles.featureList}>
                    {features.map((feature, index) => (
                        <ListItem sx={styles.featureItem} key={index}>
                            <ListIcon as={FaCheck} sx={styles.listIcon}/>
                            {feature}
                        </ListItem>
                    ))}
                </UnorderedList>}
            </Flex>
        </Box>
    );
};
